import { createFileRoute } from "@tanstack/react-router";
import { Leaf } from "lucide-react";
import { useMemo, useState } from "react";
import { sanitizeArticleHtml } from "@/lib/sanitizeArticleHtml";
import strainsData from "@/data/strains_flat_display.json";
import { formatDate } from "@/utils/formatDate";
import { PageHeader } from "@/components/PageHeader";
import { NeonCard } from "@/components/NeonCard";
import { buildMeta } from "@/lib/seo";

export const Route = createFileRoute("/medcan")({
  head: () => {
    const base = buildMeta({
      title: "MedCanReview",
      description:
        "Medizinische Cannabis-Rezensionen nach Sorte und Charge: THC/CBD, Terpene, Wirkung und Chargen-Notizen aus der Praxis.",
      path: "/medcan",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildReviewListJsonLd()),
        },
      ],
    };
  },
  component: MedCan,
});


type Entry = {
  name: string;
  dominance: "Indica" | "Sativa" | "Hybrid";
  thc_percent: string | number | null;
  cbd_percent: string | number | null;
  terpenes: string | null;
  aroma: string | null;
  crossing: string | null;
  irradiated: boolean;
  origin: string | null;
  importer: string | null;
  producer: string | null;
  medical_effects: string | null;
  review_text: string | null;
  rating: string | number | null;
  review_date: string | null;
  same_name_variant?: boolean;
  reference_profile?: {
    note?: string;
    terpenes_pct?: Record<string, number>;
    effects?: {
      anxiety_index: number;
      sedation_level: number;
      energy_level: number;
      pain_relief: number;
      appetite?: number;
      focus?: number;
    };
    phenotype?: string | null;
  } | null;
  phenotype?: string | null;
};

const entries = strainsData as Entry[];

/** Review-Strukturdaten fuer die auf der Seite sichtbaren Sorten mit Bewertung. */
function buildReviewListJsonLd() {
  const rated = entries
    .filter((e) => e.rating !== null && e.rating !== "" && !Number.isNaN(Number(e.rating)))
    .slice(0, 50);

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "MedCanReview - Cannabis-Sortenbewertungen",
    inLanguage: "de",
    itemListElement: rated.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Review",
        name: e.name,
        datePublished: e.review_date ?? undefined,
        itemReviewed: {
          "@type": "Product",
          name: e.name,
          category: "Medizinisches Cannabis",
          ...(e.producer ? { brand: { "@type": "Brand", name: e.producer } } : {}),
        },
        reviewRating: {
          "@type": "Rating",
          ratingValue: Number(e.rating),
          bestRating: 5,
          worstRating: 1,
        },
        author: { "@type": "Organization", name: "Restlicht.FYI" },
      },
    })),
  };
}

const DOMINANCES = ["Alle", "Indica", "Sativa", "Hybrid"] as const;

function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function originFlag(origin: string | null): string {
  if (!origin) return "🌍";
  const o = origin.toLowerCase();
  if (o.includes("deutsch") || o === "de") return "🇩🇪";
  if (o.includes("niederl") || o.includes("holland") || o === "nl") return "🇳🇱";
  if (o.includes("spani") || o === "es") return "🇪🇸";
  if (o.includes("marokk") || o === "ma") return "🇲🇦";
  if (o.includes("portug") || o === "pt") return "🇵🇹";
  if (o.includes("kanad") || o === "ca") return "🇨🇦";
  if (o.includes("dänem") || o.includes("danem") || o === "dk") return "🇩🇰";
  return "🌍";
}

function domStyle(d: string): React.CSSProperties {
  if (d === "Indica") return { background: "rgba(168,85,247,0.2)", color: "var(--accent-purple)" };
  if (d === "Sativa") return { background: "rgba(34,197,94,0.15)", color: "#4ade80" };
  return { background: "rgba(34,211,238,0.15)", color: "var(--accent-cyan)" };
}

function Bar({ value, max = 100, color }: { value: number; max?: number; color: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: color }} />
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div style={{ fontSize: "0.9rem", letterSpacing: "0.05em" }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ color: i <= rating ? "var(--accent-cyan)" : "var(--text-muted)" }}>★</span>
      ))}
    </div>
  );
}

function EntryCard({ entry, showReference }: { entry: Entry; showReference: boolean }) {
  const thc = toNum(entry.thc_percent);
  const cbd = toNum(entry.cbd_percent);
  const rating = toNum(entry.rating);
  const terpenes = entry.terpenes
    ? entry.terpenes.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 6)
    : [];
  const ref = showReference ? entry.reference_profile?.effects : null;
  const hasSource = entry.importer || entry.origin;

  return (
    <NeonCard as="article" style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
        <h2 style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "1rem" }}>{entry.name}</h2>
        <span style={{ ...domStyle(entry.dominance), borderRadius: 20, padding: "0.15rem 0.6rem", fontSize: "0.75rem" }}>
          {entry.dominance}
        </span>
      </div>

      {hasSource && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center", fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "-0.25rem" }}>
          {entry.origin && <span>{originFlag(entry.origin)} {entry.origin}</span>}
          {entry.importer && entry.origin && <span>·</span>}
          {entry.importer && <span>{entry.importer}</span>}
        </div>
      )}

      {thc !== null && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: 2, fontWeight: 600 }}>
            <span>THC</span><span style={{ color: "var(--accent-purple)" }}>{thc.toFixed(1)}%</span>
          </div>
          <Bar value={thc} color="var(--accent-purple)" />
        </div>
      )}
      {cbd !== null && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 2 }}>
            <span>CBD</span><span>{cbd.toFixed(1)}%</span>
          </div>
          <Bar value={cbd} color="#4ade80" />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.4rem" }}>
        {rating !== null && <Stars rating={rating} />}
        {entry.review_date && (
          <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: 500 }}>
            {formatDate(entry.review_date)}
          </span>
        )}
        {entry.irradiated && (
          <span style={{
            background: "rgba(239,68,68,0.15)",
            color: "#f87171",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 4,
            fontSize: "0.7rem",
            padding: "0.1rem 0.4rem",
          }}>BESTRAHLT</span>
        )}
      </div>

      {terpenes.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
          {terpenes.map((t, i) => (
            <span key={i} style={{
              background: "rgba(168,85,247,0.1)",
              border: "1px solid var(--border-purple)",
              borderRadius: 4,
              padding: "0.1rem 0.5rem",
              fontSize: "0.75rem",
              color: "var(--text-secondary)",
            }}>{t}</span>
          ))}
        </div>
      )}

      {entry.aroma && (
        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
          <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginRight: "0.4rem" }}>Aroma:</span>
          {entry.aroma}
        </div>
      )}

      {entry.medical_effects && (
        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
          <span style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem", marginRight: "0.4rem" }}>
            Wirkung:
          </span>
          {entry.medical_effects}
        </div>
      )}

      {entry.review_text && (
        <p className="article-content" style={{
          fontStyle: "italic",
          color: "var(--text-muted)",
          fontSize: "0.85rem",
          marginTop: "0.25rem",
        }} dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(entry.review_text) }} />
      )}

      <TerpeneSection terpenes={entry.reference_profile?.terpenes_pct ?? null} />
      <EffectSection effects={ref ?? null} />
      <GeneticsSection crossing={entry.crossing} phenotype={entry.reference_profile?.phenotype ?? entry.phenotype ?? null} />
    </NeonCard>
  );
}

function SectionShell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: "1px solid var(--border-purple)", marginTop: "0.75rem", paddingTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <div style={{ color: "var(--text-muted)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function NeonSkeleton({ height = 8, width = "100%" }: { height?: number; width?: number | string }) {
  return <span className="medcan-skeleton" style={{ height, width }} aria-hidden="true" />;
}

function TerpeneSection({ terpenes }: { terpenes: Record<string, number> | null }) {
  const entries = terpenes
    ? Object.entries(terpenes).sort((a, b) => b[1] - a[1]).slice(0, 6)
    : [];
  return (
    <SectionShell label="Terpen-Profil">
      {entries.length > 0
        ? entries.map(([name, pct]) => (
            <div key={name}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 2 }}>
                <span style={{ textTransform: "capitalize" }}>{name}</span>
                <span>{pct.toFixed(1)}%</span>
              </div>
              <Bar value={pct} max={Math.max(10, pct)} color="var(--accent-purple)" />
            </div>
          ))
        : Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <NeonSkeleton height={10} width={60 + (i % 3) * 20} />
                <NeonSkeleton height={10} width={32} />
              </div>
              <NeonSkeleton height={6} />
            </div>
          ))}
    </SectionShell>
  );
}

type EffectsData = NonNullable<NonNullable<Entry["reference_profile"]>["effects"]>;

function EffectSection({ effects }: { effects: EffectsData | null }) {
  const rows: ReadonlyArray<readonly [string, number | null, string]> = [
    ["Angst", effects?.anxiety_index ?? null, "#f87171"],
    ["Sedierung", effects?.sedation_level ?? null, "var(--accent-purple)"],
    ["Energie", effects?.energy_level ?? null, "var(--accent-cyan)"],
    ["Schmerz", effects?.pain_relief ?? null, "#fb923c"],
    ["Appetit", effects?.appetite ?? null, "#4ade80"],
    ["Fokus", effects?.focus ?? null, "#facc15"],
  ];
  return (
    <SectionShell label="Wirkprofil">
      {rows.map(([label, val, color]) => (
        <div key={label}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 2 }}>
            <span>{label}</span>
            <span>{val !== null ? val.toFixed(1) : ""}</span>
          </div>
          {val !== null
            ? <Bar value={val} max={10} color={color} />
            : <NeonSkeleton height={6} />}
        </div>
      ))}
    </SectionShell>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      background: "rgba(168,85,247,0.1)",
      border: "1px solid var(--border-purple)",
      borderRadius: 4,
      padding: "0.15rem 0.55rem",
      fontSize: "0.75rem",
      color: "var(--text-secondary)",
    }}>{children}</span>
  );
}

function GeneticsSection({ crossing, phenotype }: { crossing: string | null; phenotype: string | null }) {
  const parents = crossing
    ? crossing.split(/\s*[x×]\s*/i).map((s) => s.trim()).filter(Boolean)
    : [];
  return (
    <SectionShell label="Genetik und Lineage">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Eltern</span>
          {parents.length > 0
            ? <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>{parents.map((p, i) => <Pill key={i}>{p}</Pill>)}</div>
            : <NeonSkeleton height={20} width="80%" />}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Phänotyp</span>
          {phenotype
            ? <div><Pill>{phenotype}</Pill></div>
            : <NeonSkeleton height={20} width="70%" />}
        </div>
      </div>
    </SectionShell>
  );
}

function MedCan() {
  const [dominance, setDominance] = useState<string>("Alle");

  const nameCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of entries) m.set(e.name, (m.get(e.name) ?? 0) + 1);
    return m;
  }, []);

  const sorted = useMemo(() => {
    const list = dominance === "Alle" ? entries : entries.filter((e) => e.dominance === dominance);
    return [...list].sort((a, b) => {
      const n = a.name.localeCompare(b.name, "de");
      if (n !== 0) return n;
      return (toNum(b.thc_percent) ?? 0) - (toNum(a.thc_percent) ?? 0);
    });
  }, [dominance]);

  return (
    <div data-section="medcan">
      <PageHeader icon={<Leaf size={28} />} title="MedCanReview" />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.5rem" }}>
        {DOMINANCES.map((d) => {
          const active = d === dominance;
          if (active) {
            return (
              <button
                key={d}
                onClick={() => setDominance(d)}
                style={{
                  borderRadius: 6,
                  padding: "0.4rem 0.9rem",
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  background: "var(--accent)",
                  color: "var(--ink-on-accent)",
                  border: "1px solid var(--accent)",
                  fontWeight: 600,
                }}
              >
                {d}
              </button>
            );
          }
          return (
            <NeonCard
              as="button"
              key={d}
              interactive
              onClick={() => setDominance(d)}
              style={{
                padding: "0.4rem 0.9rem",
                fontSize: "0.85rem",
                color: "var(--text-secondary)",
                fontWeight: 400,
                background: "transparent",
              }}
            >
              {d}
            </NeonCard>
          );
        })}
      </div>

      <div className="home-grid">
        {sorted.map((e, i) => (
          <EntryCard
            key={`${e.name}-${e.review_date ?? i}-${i}`}
            entry={e}
            showReference={(nameCounts.get(e.name) ?? 0) === 1}
          />
        ))}
      </div>
    </div>
  );
}

